import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { SidebarProvider } from "./components/app/SidebarContext";
import AuthLayout from "./app/(auth)/layout";
import DashboardLayout from "./app/(dashboard)/layout";
import NotFound from "./app/not-found";
import { ROUTES } from "./lib/routes";

// Lazy load auth pages
const LoginPage = lazy(() => import("./app/(auth)/login/page"));
const ForgotPasswordPage = lazy(
  () => import("./app/(auth)/forgot-password/page"),
);
const VerifyOtpPage = lazy(() => import("./app/(auth)/verify-otp/page"));
const CreatePasswordPage = lazy(
  () => import("./app/(auth)/create-password/page"),
);
const ChangePasswordPage = lazy(
  () => import("./app/(auth)/change-password/page"),
);

// Lazy load dashboard pages
const DashboardPage = lazy(() => import("./app/(dashboard)/page"));
const OrderPage = lazy(() => import("./app/(dashboard)/order/page"));

const CreateOrderPage = lazy(
  () => import("./app/(dashboard)/order/create/page"),
);
const EditOrderPage = lazy(
  () => import("./app/(dashboard)/order/[id]/edit/page"),
);
const ViewOrderPage = lazy(() => import("./app/(dashboard)/order/[id]/page"));
const ProductPage = lazy(() => import("./app/(dashboard)/product/page"));
const ViewProductPage = lazy(() => import("./app/(dashboard)/product/[id]/page"));
const ManufacturersPage = lazy(
  () => import("./app/(dashboard)/manufactures/page"),
);
const AddManufacturerPage = lazy(
  () => import("./app/(dashboard)/manufactures/add/page"),
);
const ViewManufacturerPage = lazy(
  () => import("./app/(dashboard)/manufactures/[id]/page"),
);
const EditManufacturerPage = lazy(
  () => import("./app/(dashboard)/manufactures/[id]/edit/page"),
);
const CustomersPage = lazy(() => import("./app/(dashboard)/customers/page"));
const AddCustomerPage = lazy(
  () => import("./app/(dashboard)/customers/add/page"),
);
const ViewCustomerPage = lazy(
  () => import("./app/(dashboard)/customers/[id]/page"),
);
const EditCustomerPage = lazy(
  () => import("./app/(dashboard)/customers/[id]/edit/page"),
);
const PortfolioUsersPage = lazy(() => import("./app/(dashboard)/portfolio/users/page"));
const PortfolioUserDetailPage = lazy(() => import("./app/(dashboard)/portfolio/users/[id]/page"));
const PortfolioTransactionsPage = lazy(() => import("./app/(dashboard)/portfolio/transactions/page"));
const PortfolioTransactionDetailPage = lazy(() => import("./app/(dashboard)/portfolio/transactions/[id]/page"));
const PayoutsPage = lazy(() => import("./app/(dashboard)/payouts/page"));
const ViewPayoutPage = lazy(
  () => import("./app/(dashboard)/payouts/[id]/page"),
);
const RepairPage = lazy(() => import("./app/(dashboard)/repair-management/page"));
const ViewRepairPage = lazy(
  () => import("./app/(dashboard)/repair-management/[id]/page"),
);
const AccountSettingsPage = lazy(
  () => import("./app/(dashboard)/account-settings/page"),
);
const SettingsAccountPage = lazy(
  () => import("./app/(dashboard)/settings/account/page"),
);
const PricingSettingsPage = lazy(
  () => import("./app/(dashboard)/settings/pricing/page"),
);
const BroadcastManagementPage = lazy(
  () => import("./app/(dashboard)/broadcast-management/page"),
);
const AddBroadcastPage = lazy(
  () => import("./app/(dashboard)/broadcast-management/add/page"),
);
const CMSPage = lazy(() => import("./app/(dashboard)/cms/page"));
const TermsPage = lazy(() => import("./app/(dashboard)/cms/terms/page"));
const PrivacyPage = lazy(() => import("./app/(dashboard)/cms/privacy/page"));
const HelpPage = lazy(() => import("./app/(dashboard)/cms/help/page"));
const SubcategoriesPage = lazy(
  () => import("./app/(dashboard)/subcategories/page"),
);

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <SidebarProvider>
      <BrowserRouter>
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route
              path={ROUTES.AUTH.LOGIN}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.AUTH.FORGOT_PASSWORD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.AUTH.VERIFY_OTP}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <VerifyOtpPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.AUTH.CREATE_PASSWORD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CreatePasswordPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.AUTH.CHANGE_PASSWORD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ChangePasswordPage />
                </Suspense>
              }
            />
          </Route>

          {/* Dashboard Routes */}
          <Route element={<DashboardLayout />}>
            <Route
              path={ROUTES.DASHBOARD.HOME}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.ORDER_MANAGEMENT}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OrderPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.ORDER_CREATE}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CreateOrderPage />
                </Suspense>
              }
            />
            <Route
              path="/order/:id/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <EditOrderPage />
                </Suspense>
              }
            />
            <Route
              path="/order/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewOrderPage />
                </Suspense>
              }
            />

            <Route
              path={ROUTES.DASHBOARD.PRODUCT_MANAGEMENT}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProductPage />
                </Suspense>
              }
            />
            <Route
              path="/product/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewProductPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.MANUFACTURES}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ManufacturersPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.MANUFACTURES_ADD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AddManufacturerPage />
                </Suspense>
              }
            />
            <Route
              path="/manufactures/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewManufacturerPage />
                </Suspense>
              }
            />
            <Route
              path="/manufactures/:id/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <EditManufacturerPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CUSTOMERS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CustomersPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.SUBCATEGORIES}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SubcategoriesPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CUSTOMERS_ADD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AddCustomerPage />
                </Suspense>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewCustomerPage />
                </Suspense>
              }
            />
            <Route
              path="/customers/:id/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <EditCustomerPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.PORTFOLIO_USERS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PortfolioUsersPage />
                </Suspense>
              }
            />
            <Route
              path="/portfolio/users/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PortfolioUserDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.PORTFOLIO_TRANSACTIONS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PortfolioTransactionsPage />
                </Suspense>
              }
            />
            <Route
              path="/portfolio/transactions/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PortfolioTransactionDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.PAYOUTS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PayoutsPage />
                </Suspense>
              }
            />
            <Route
              path="/payouts/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewPayoutPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.REPAIR_MANAGEMENT}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RepairPage />
                </Suspense>
              }
            />
            <Route
              path="/repair-management/:id"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ViewRepairPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.ACCOUNT_SETTINGS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AccountSettingsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.SETTINGS_ACCOUNT}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SettingsAccountPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.SETTINGS_PRICING}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PricingSettingsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CMS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CMSPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CMS_TERMS}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <TermsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CMS_PRIVACY}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PrivacyPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.CMS_HELP}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <HelpPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.BROADCAST_MANAGEMENT}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <BroadcastManagementPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DASHBOARD.BROADCAST_ADD}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AddBroadcastPage />
                </Suspense>
              }
            />
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </SidebarProvider>
  );
}
